import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, MapPin, Clock, Users, Repeat } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { formatRecurringDays } from '../utils/dateFormat';


const DUTCH_MONTHS = [
  'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni',
  'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'
];

const DUTCH_DAYS = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];

const ActivityCalendar = ({ onClose }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedActivities, setSelectedActivities] = useState([]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;

  useEffect(() => {
    fetchCalendarData();
  }, [year, month]);

  const fetchCalendarData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/activities/calendar?year=${year}&month=${month}`);
      setCalendarData(response.data);
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(year, month - 2, 1));
    setSelectedDate(null);
    setSelectedActivities([]);
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month, 1));
    setSelectedDate(null);
    setSelectedActivities([]);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(null);
    setSelectedActivities([]);
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    const day = new Date(year, month - 1, 1).getDay();
    // Convert Sunday (0) to 7 for Monday-first week
    return day === 0 ? 6 : day - 1;
  };

  const handleDateClick = (day) => {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr);
    
    if (calendarData?.activities_by_date?.[dateStr]) {
      setSelectedActivities(calendarData.activities_by_date[dateStr]);
    } else {
      setSelectedActivities([]);
    }
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    const days = [];
    
    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-10 md:h-14" />);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const activitiesOnDay = calendarData?.activities_by_date?.[dateStr] || [];
      const hasActivities = activitiesOnDay.length > 0;
      const isToday = dateStr === todayStr;
      const isSelected = dateStr === selectedDate;
      const isPast = dateStr < todayStr;
      
      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(day)}
          className={`
            h-10 md:h-14 rounded-xl flex flex-col items-center justify-center relative transition-all
            ${isSelected ? 'bg-brand text-white shadow-lg scale-105' : ''}
            ${isToday && !isSelected ? 'bg-brand/10 text-brand font-bold ring-2 ring-brand' : ''}
            ${!isSelected && !isToday && hasActivities ? 'bg-accent/10 hover:bg-accent/20' : ''}
            ${!isSelected && !isToday && !hasActivities ? 'hover:bg-slate-100' : ''}
            ${isPast && !isSelected ? 'text-muted-foreground' : ''}
          `}
        >
          <span className="text-sm md:text-base">{day}</span>
          {hasActivities && (
            <div className="flex gap-0.5 mt-0.5">
              {activitiesOnDay.slice(0, 3).map((_, idx) => (
                <div 
                  key={idx} 
                  className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-white' : 'bg-brand'}`}
                />
              ))}
              {activitiesOnDay.length > 3 && (
                <span className={`text-[10px] ${isSelected ? 'text-white' : 'text-brand'}`}>+</span>
              )}
            </div>
          )}
        </button>
      );
    }
    
    return days;
  };

  const activityTypeEmoji = {
    sport: '⚽',
    kunst: '🎨',
    muziek: '🎵',
    eten: '🍳',
    natuur: '🌿',
    sociaal: '☕',
    educatie: '📚',
    kinderen: '👨‍👩‍👧',
    ouderen: '🤝',
    anders: '✨'
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-5 h-5 text-brand" />
          <h2 className="font-heading font-bold text-lg text-primary">Agenda</h2>
        </div>
        {onClose && (
          <Button variant="ghost" size="sm" onClick={onClose}>
            Sluiten
          </Button>
        )}
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-between bg-slate-50 rounded-xl p-3">
        <Button variant="ghost" size="icon" onClick={goToPreviousMonth}>
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div className="text-center">
          <h3 className="font-heading font-bold text-lg text-primary">
            {DUTCH_MONTHS[month - 1]} {year}
          </h3>
          <button 
            onClick={goToToday}
            className="text-xs text-brand hover:underline"
          >
            Naar vandaag
          </button>
        </div>
        <Button variant="ghost" size="icon" onClick={goToNextMonth}>
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Stats */}
      {calendarData && (
        <div className="flex gap-3">
          <Badge variant="secondary" className="rounded-full px-3 py-1">
            📅 {calendarData.total_activities} activiteiten
          </Badge>
          <Badge variant="secondary" className="rounded-full px-3 py-1 bg-green-100 text-green-700">
            🔜 {calendarData.upcoming_count} komende
          </Badge>
          <Badge variant="secondary" className="rounded-full px-3 py-1 bg-slate-100">
            ✓ {calendarData.past_count} geweest
          </Badge>
        </div>
      )}

      {/* Calendar Grid */}
      {loading ? (
        <div className="space-y-2">
          <div className="grid grid-cols-7 gap-1">
            {[...Array(7)].map((_, i) => (
              <Skeleton key={i} className="h-8" />
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {[...Array(35)].map((_, i) => (
              <Skeleton key={i} className="h-14" />
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl p-3 border border-slate-100">
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {DUTCH_DAYS.map((day) => (
              <div key={day} className="h-8 flex items-center justify-center">
                <span className="text-xs font-semibold text-muted-foreground">{day}</span>
              </div>
            ))}
          </div>
          
          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1">
            {renderCalendarDays()}
          </div>
        </div>
      )}

      {/* Selected Date Activities */}
      {selectedDate && (
        <Card className="p-4 rounded-2xl bg-white border border-primary/5">
          <h4 className="font-heading font-bold text-primary mb-3">
            {new Date(selectedDate + 'T12:00:00').toLocaleDateString('nl-NL', { 
              weekday: 'long', 
              day: 'numeric', 
              month: 'long' 
            })}
          </h4>
          
          {selectedActivities.length > 0 ? (
            <div className="space-y-3">
              {selectedActivities.map((activity) => (
                <Link 
                  key={activity.id} 
                  to={`/activity/${activity.id}`}
                  className="block"
                >
                  <Card className="p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors border-0">
                    <div className="flex items-start gap-3">
                      <div className="text-2xl">
                        {activityTypeEmoji[activity.activity_type] || '✨'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h5 className="font-semibold text-primary truncate">
                          {activity.title}
                        </h5>
                        <div className="flex flex-wrap gap-2 mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {activity.time}
                          </span>
                          {activity.recurring && activity.recurring_days?.length > 0 && (
                            <span className="flex items-center gap-1 text-brand font-medium">
                              <Repeat className="w-3 h-3" />
                              {formatRecurringDays(activity.recurring_days, 'nl')}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {activity.wijk}
                          </span>
                          {activity.participants_count > 0 && (
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {activity.participants_count}
                            </span>
                          )}
                        </div>
                      </div>
                      {activity.is_live && (
                        <Badge className="bg-red-500 text-white shrink-0">LIVE</Badge>
                      )}
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">
              Geen activiteiten op deze dag
            </p>
          )}
        </Card>
      )}
    </div>
  );
};

export default ActivityCalendar;
